import React from 'react';
import { SEOHead } from '@/components/seo';
import { Header, Footer } from '@/components/public';

const SITE_NAME = 'StartEase Agent';
const BASE_URL = 'https://starteaseai.com';

const PrivacyPage = () => {
  return (
    <div className="min-h-screen bg-white" data-testid="privacy-page">
      <SEOHead
        title="Privacy Policy | StartEase Agent"
        description="How StartEase Agent — a business solutions platform — collects, uses, shares and protects your information across formation, compliance and partner solutions."
        siteName={SITE_NAME}
        canonical={`${BASE_URL}/privacy`}
      />

      <Header />

      {/* Hero */}
      <section className="bg-[#070d0b] border-b border-white/10 py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <span className="inline-block text-[11px] font-semibold uppercase tracking-[0.22em] text-emerald-400 mb-4">Legal</span>
          <h1 className="text-4xl md:text-5xl text-white mb-4">Privacy Policy</h1>
          <p className="text-slate-400">Last updated: June 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">1. Introduction</h2>
            <p className="text-slate-600 mb-6">
              StartEase Agent ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our business solutions platform — including services we deliver directly and solutions we connect you with through trusted partners.
            </p>

            <h2 className="text-2xl font-semibold text-slate-900 mb-4">2. Information We Collect</h2>
            <p className="text-slate-600 mb-4">We collect information that you provide directly to us, including:</p>
            <ul className="list-disc list-inside text-slate-600 mb-6 space-y-2">
              <li>Personal identification information (name, email address, phone number)</li>
              <li>Business information (company name, business address, business type, markets of interest)</li>
              <li>Details about the solutions you're looking for or want us to set up</li>
              <li>Payment information (processed securely through our payment provider)</li>
              <li>Government identification numbers required for filings, where applicable</li>
              <li>Communication records when you contact our support team or request a consultation</li>
            </ul>

            <h2 className="text-2xl font-semibold text-slate-900 mb-4">3. How We Use Your Information</h2>
            <p className="text-slate-600 mb-4">We use your information to:</p>
            <ul className="list-disc list-inside text-slate-600 mb-6 space-y-2">
              <li>Deliver the services you request and process your filings</li>
              <li>Recommend and connect you with the right technology and specialist partners</li>
              <li>Help implement and set up the solutions you adopt</li>
              <li>Communicate with you about your orders, consultations and services</li>
              <li>Send important notices about deadlines and compliance requirements</li>
              <li>Improve our services and develop new features</li>
              <li>Comply with legal obligations and prevent fraud</li>
            </ul>

            <h2 className="text-2xl font-semibold text-slate-900 mb-4">4. Information Sharing</h2>
            <p className="text-slate-600 mb-4">We may share your information with:</p>
            <ul className="list-disc list-inside text-slate-600 mb-6 space-y-2">
              <li>Partner companies, when you ask us to connect you with or set up their solution — limited to what is needed to facilitate that introduction or onboarding</li>
              <li>Government, state and regulatory agencies as required for filings</li>
              <li>Service providers who assist in our operations (securely and under contract)</li>
              <li>Legal authorities when required by law or to protect our rights</li>
            </ul>
            <p className="text-slate-600 mb-6">
              We do not sell your personal information to third parties for marketing purposes. Where we connect you with a partner, that partner's use of your information is governed by their own privacy policy.
            </p>

            <h2 className="text-2xl font-semibold text-slate-900 mb-4">5. Data Security</h2>
            <p className="text-slate-600 mb-6">
              We implement industry-standard security measures to protect your information, including encryption, secure servers, and access controls. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
            </p>

            <h2 className="text-2xl font-semibold text-slate-900 mb-4">6. Data Retention</h2>
            <p className="text-slate-600 mb-6">
              We retain your information for as long as necessary to provide our services and comply with legal obligations. Formation and compliance documents may be retained to support ongoing obligations. You may request deletion of your account, subject to our legal retention requirements.
            </p>

            <h2 className="text-2xl font-semibold text-slate-900 mb-4">7. Your Rights</h2>
            <p className="text-slate-600 mb-4">You have the right to:</p>
            <ul className="list-disc list-inside text-slate-600 mb-6 space-y-2">
              <li>Access the personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of your information (subject to legal requirements)</li>
              <li>Opt out of marketing communications</li>
              <li>Request a copy of your data in a portable format</li>
            </ul>

            <h2 className="text-2xl font-semibold text-slate-900 mb-4">8. Cookies and Tracking</h2>
            <p className="text-slate-600 mb-6">
              We use cookies and similar technologies to enhance your experience, analyze usage patterns, and improve our services. You can control cookie settings through your browser preferences.
            </p>

            <h2 className="text-2xl font-semibold text-slate-900 mb-4">9. Third-Party Links &amp; Partners</h2>
            <p className="text-slate-600 mb-6">
              Our website links to third-party and partner websites. We are not responsible for the privacy practices of these external sites and partners. When you adopt a partner solution, your information is handled under that partner's own privacy policy. We encourage you to review their policies before providing any information.
            </p>

            <h2 className="text-2xl font-semibold text-slate-900 mb-4">10. Children's Privacy</h2>
            <p className="text-slate-600 mb-6">
              Our services are not intended for individuals under 18 years of age. We do not knowingly collect personal information from children.
            </p>

            <h2 className="text-2xl font-semibold text-slate-900 mb-4">11. International Users</h2>
            <p className="text-slate-600 mb-6">
              We support businesses operating across the United States, United Kingdom, United Arab Emirates, Singapore and Canada, and we may work with partners and service providers in different countries. By using our services, you consent to your information being transferred and processed in the countries where we and our partners operate, in line with applicable law.
            </p>

            <h2 className="text-2xl font-semibold text-slate-900 mb-4">12. Changes to This Policy</h2>
            <p className="text-slate-600 mb-6">
              We may update this Privacy Policy from time to time. We will notify you of significant changes via email or through our website. Your continued use of our services after changes constitutes acceptance of the updated policy.
            </p>

            <h2 className="text-2xl font-semibold text-slate-900 mb-4">13. Contact Us</h2>
            <p className="text-slate-600 mb-6">
              If you have questions or concerns about this Privacy Policy or our data practices, please contact us at:<br />
              Email: hi@starteaseai.com<br />
              Phone: +1 (205) 624-5819<br />
              Website: starteaseai.com
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPage;
